import React, { useState, useEffect, useRef } from 'react';
import Dropzone from 'react-dropzone';
import { useDrag, useDrop, useDragLayer, DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { useNavigate, useBlocker } from 'react-router-dom';
import { doc, collection } from 'firebase/firestore';
import { arrayMoveImmutable } from 'array-move';
import { useAuth } from './authContext';
import { auth } from '../firebase.js';
import { submitPhoto, db, appHandoff } from '../firebase.js';
import { deleteProduct } from '../databaseManager';
import { formatCurrentTime, encodeFileAsBase64, convertHEICToJPEG, resizeImage, generateUUID, isHeicFile } from './sharedUtils.js';
import ProductEditGeneric from './productEditGeneric.js'; // ProductEdit component is included here
import UploaderProductPreview from './products/UploaderProductPreview.js';
import EXIF from 'exif-js';
import {
  Button,
  Box,
  Container,
  Typography,
  TextField,
  CircularProgress,
  IconButton,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from '@mui/material';
import ConfirmDeleteDialog from './confirmDeleteDialog.js';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMoveOutlined';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import Menu from '@mui/material/Menu';
import '../shopik.webapp.uploader.css';

// Cap how many products "Submit All" sends in one go, so the browser isn't
// left driving dozens of uploads at once. Any groups past the cap are simply
// left pending for the user to run again once the current batch finishes.
const SUBMIT_ALL_BATCH_SIZE = 10;

// A utility function to detect if the device is touch-enabled
const isTouchDevice = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

const DraggableImage = ({ image, index, groups, groupIndex, imageIndex, handleImageClick, handleRemoveImage, handleMoveImage, insertNewGroup, moveImage, bulkMode }) => {
  const ref = useRef(null);
  const [moveMenuAnchor, setMoveMenuAnchor] = useState(null);

  const [{ isDragging: isCurrentlyDragging }, drag] = useDrag({
    type: 'image',
    item: { index, groupIndex, image },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: 'image',
    hover: (draggedItem) => {
      if (draggedItem.index !== index || draggedItem.groupIndex !== groupIndex) {
        moveImage(draggedItem.index, index, draggedItem.groupIndex, groupIndex);
        draggedItem.index = index;
        draggedItem.groupIndex = groupIndex;
      }
    },
  });

  // Attach drag and drop refs
  drag(drop(ref));

  if (!image || !image.preview) {
    return null; // Early return if image or image.preview is undefined
  }

  const canSplit = bulkMode && imageIndex < groups[groupIndex].files.length - 1;

  return (
    <div
      ref={ref}
      className={`su-thumb${isCurrentlyDragging ? ' is-dragging' : ''}`}
      title="Drag to reorder or move to another product"
    >
      <img
        src={image.preview}
        alt="preview"
        onClick={() => handleImageClick(groupIndex, imageIndex)}
        onDragStart={(e) => e.preventDefault()} // Prevent default browser drag behavior
      />
      <span className="su-thumb-index">{imageIndex + 1}</span>
      <div className="su-thumb-actions">
        {canSplit && (
          <IconButton
            size="small"
            title="Split product here"
            onClick={() => insertNewGroup(groupIndex, imageIndex)}
          >
            <ContentCutIcon />
          </IconButton>
        )}
        {bulkMode && groups.length > 1 && (
          <IconButton
            size="small"
            title="Move to another product"
            onClick={(e) => setMoveMenuAnchor(e.currentTarget)}
          >
            <DriveFileMoveIcon />
          </IconButton>
        )}
        <IconButton
          size="small"
          title="Remove image"
          onClick={() => {
            if (window.confirm("Are you sure you want to remove this image?")) {
              handleRemoveImage(image, groupIndex);
            }
          }}
        >
          <DeleteIcon />
        </IconButton>
      </div>
      <Menu
        anchorEl={moveMenuAnchor}
        open={Boolean(moveMenuAnchor)}
        onClose={() => setMoveMenuAnchor(null)}
      >
        {groups.map((_, idx) => (
          idx !== groupIndex && (
            <MenuItem
              key={idx}
              onClick={() => {
                handleMoveImage(image, groupIndex, idx);
                setMoveMenuAnchor(null);
              }}
            >
              Product {idx + 1}
            </MenuItem>
          )
        ))}
      </Menu>
    </div>
  );
};

// Custom DragLayer for Drag Preview
const CustomDragLayer = () => {
  const { isDragging, item, currentOffset } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    item: monitor.getItem(),
    currentOffset: monitor.getClientOffset(),
  }));

  if (!isDragging || !currentOffset || !item || !item.image || !item.image.preview) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        pointerEvents: 'none',
        left: currentOffset.x,
        top: currentOffset.y,
        zIndex: 1000,
        transform: 'translate(-50%, -50%)',
      }}
    >
      <div
        style={{
          padding: '5px',
          background: 'rgba(255, 255, 255, 0.9)',
          borderRadius: '4px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        }}
      >
        <img src={item.image.preview} alt="drag-preview" style={{ width: '60px', height: '60px', borderRadius: '4px' }} />
      </div>
    </div>
  );
};

const DroppableImageList = ({ items, groups, groupIndex, moveImage, handleImageClick, handleRemoveImage, handleMoveImage, insertNewGroup, bulkMode }) => (
  <div className="su-photo-grid">
    {items.map((image, index) => (
      <DraggableImage
        key={`item-${index}`}
        index={index}
        image={image}
        groupIndex={groupIndex}
        imageIndex={index}
        groups={groups}
        handleImageClick={handleImageClick}
        handleRemoveImage={handleRemoveImage}
        handleMoveImage={handleMoveImage}
        insertNewGroup={insertNewGroup}
        moveImage={moveImage}
        bulkMode={bulkMode}
      />
    ))}
  </div>
);

function Uploader({ uploaderStyle }) {
  const { customer, user, shopikUser, logout } = useAuth();
  const [groups, setGroups] = useState([]);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [processingIndex, setProcessingIndex] = useState(-1);
  const [newPath, setNewPath] = useState('');
  const [defaultPhotoCount, setDefaultPhotoCount] = useState(uploaderStyle === 1 ? 4 : 10);
  const [defaultTime, setDefaultTime] = useState(60000);
  const [defaultText, setDefaultText] = useState('');
  const navigate = useNavigate();
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [isSubmittingAll, setIsSubmittingAll] = useState(false);

  // Tracks the in-flight upload (if any) so we can warn on / clean up an early exit.
  // A ref (not state) because it must reflect the latest value inside the async
  // upload loop and inside the useBlocker callback without waiting on a re-render.
  const activeUploadRef = useRef({ groupIndex: -1, productDocument: '' });
  const submittingGroupsRef = useRef(new Set());
  const [deletingOnLeave, setDeletingOnLeave] = useState(false);
  // Groups whose card has been switched to the full editor via the pencil button.
  const [editingGroups, setEditingGroups] = useState(new Set());

  // Native "are you sure you want to leave" prompt for tab close / refresh mid-upload.
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (activeUploadRef.current.groupIndex === -1) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // In-app navigation (SPA route change) mid-upload: block and offer to stay or
  // leave & discard the partially-uploaded product. Single-item mode is exempt —
  // it intentionally navigates away itself the moment upload starts (see
  // handleSubmitGroup), so blocking here would also block that own navigation.
  const blocker = useBlocker(() => bulkMode && activeUploadRef.current.groupIndex !== -1);

  const handleConfirmLeave = async () => {
    const { productDocument } = activeUploadRef.current;
    setDeletingOnLeave(true);
    try {
      if (productDocument) {
        await deleteProduct(customer.id, productDocument);
      }
    } catch (error) {
      console.error('Error deleting incomplete product on early leave:', error);
    } finally {
      activeUploadRef.current = { groupIndex: -1, productDocument: '' };
      setDeletingOnLeave(false);
      blocker.proceed?.();
    }
  };

  useEffect(() => {
    const currentPath = window.location.pathname;
    const pathParts = currentPath.split('/');
    const lastPart = pathParts[pathParts.length - 1];
    setNewPath(`/m/products/${lastPart}`);
  }, []);

  // Once a product has been submitted, block adding more photos/products.
  // Single-item mode navigates away immediately anyway; bulk mode stays on
  // this page (see "View My Products" button below) so the user can review
  // what's finished without it re-opening the add-photos dropzone.
  const anyProductSubmitted = groups.some((group) => group.productDocument);

  // Groups that still need to be submitted (used to size/label/disable "Submit All").
  const pendingGroupIndices = groups.reduce((indices, group, index) => {
    if (!group.productDocument && group.files.length > 0) indices.push(index);
    return indices;
  }, []);

  let dateString = formatCurrentTime();
  const moveImage = (fromIndex, toIndex, fromGroupIndex, toGroupIndex) => {
    setGroups((prevGroups) => {
      const newGroups = prevGroups.map((group) => ({ ...group, files: [...group.files] }));
      const [movedImage] = newGroups[fromGroupIndex].files.splice(fromIndex, 1);
      newGroups[toGroupIndex].files.splice(toIndex, 0, movedImage);
      return newGroups;
    });
  };

  let sku = (
    dateString.split('+')[0]
      .replace(/,/g, '')
      .replace(/:/g, '')
    + generateUUID().replace(/-/g, '').substring(0, 4)
  );

  const goToPlanPage = () => {
    navigate('/plans');
  };

  const handleDrop = async (acceptedFiles) => {
    setLoadingFiles(true);
    
    const filesWithTimeAndGroup = await Promise.all(acceptedFiles.map(async (file, index) => {
      let convertedFile = file;
      if (isHeicFile(file)) {
        const jpegBlob = await convertHEICToJPEG(file);
        if (jpegBlob) {
          convertedFile = new File([jpegBlob], file.name.replace(/\.[^/.]+$/, ".jpg"), { type: "image/jpeg" });
        }
      }

      // Resize large images to prevent page breaking
      convertedFile = await resizeImage(convertedFile);

      let exifDate = null;
      if (file.type.startsWith('image/')) {
        try {
          const data = await new Promise((resolve) => {
            EXIF.getData(file, function () {
              resolve(EXIF.getAllTags(this));
            });
          });
          if (data && data.DateTimeOriginal) {
            exifDate = parseExifDate(data.DateTimeOriginal);
            if (!exifDate) {
              console.error(`Failed to parse EXIF date for: ${file.name}. Using lastModified date.`);
            }
          } 
        } catch (error) {
          console.error(`Error extracting EXIF data for: ${file.name}`, error);
        } 
      }
      
      const finalTime = exifDate ? exifDate.getTime() : convertedFile.lastModified;
  
      // Check if the file has a name pattern like x-y (e.g., 1-1, 2-1, 2-2)
      const match = file.name.match(/^(\d+)-(\d+)/);
      const groupName = match ? match[1] : null; // Extract the group number if it exists
  
      return {
        file: convertedFile,
        time: finalTime,
        groupName: groupName,  // Include the group name if detected
        preview: URL.createObjectURL(convertedFile),
      };
    }));
  
    // Sort by time
    filesWithTimeAndGroup.sort((a, b) => a.time - b.time);

    if (!bulkMode) {
      // Normal mode: always a single product, never auto-split by name/time patterns.
      setGroups(prevGroups => {
        if (prevGroups.length > 0) {
          const newGroups = prevGroups.map((group) => ({ ...group, files: [...group.files] }));
          newGroups[0].files.push(...filesWithTimeAndGroup);
          return newGroups;
        }
        return [{ files: filesWithTimeAndGroup, text: defaultText, productDocument: '' }];
      });
      setLoadingFiles(false);
      return;
    }

    const newGroups = [];
    let currentGroup = [];
    let photoCounter = 0;
  
    filesWithTimeAndGroup.forEach((file, index) => {
      if (file.groupName) {
        // Group by the name pattern (e.g., 1-1, 1-2 will be grouped together)
        if (currentGroup.length === 0 || file.groupName === currentGroup[0].groupName) {
          currentGroup.push(file);
          photoCounter++;
        } else {
          // Push the current group and start a new one
          newGroups.push({ files: currentGroup, text: `Group ${currentGroup[0].groupName}`, productDocument: '' });
          currentGroup = [file];
          photoCounter = 1;
        }
      } else {
        // Group by timestamp if no name pattern is detected
        if (currentGroup.length === 0 || 
            ((file.time - filesWithTimeAndGroup[index - 1].time < defaultTime) && photoCounter < defaultPhotoCount)) {
          currentGroup.push(file);
          photoCounter++;
        } else {
          newGroups.push({ files: currentGroup, text: defaultText, productDocument: '' });
          currentGroup = [file];
          photoCounter = 1;
        }
      }
    });
  
    if (currentGroup.length) {
      newGroups.push({ files: currentGroup, text: currentGroup[0].groupName ? `Group ${currentGroup[0].groupName}` : defaultText, productDocument: '' });
    }
  
    setGroups(prevGroups => [...prevGroups, ...newGroups]);
    setLoadingFiles(false)
  };

  // Helper function to correctly parse EXIF date strings
  const parseExifDate = (exifDateStr) => {
    // EXIF date format: "YYYY:MM:DD HH:MM:SS"
    const exifRegex = /^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})$/;
    const match = exifDateStr.match(exifRegex);
  
    if (match) {
      // Extract date parts and create a Date object
      const [ , year, month, day, hour, minute, second ] = match;
      return new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
    }
  
    return null; // Return null if parsing failed
  };

  const handleMoveImage = (image, fromGroupIndex, toGroupIndex) => {
    const newGroups = groups.map(group => ({ ...group, files: [...group.files] }));
    newGroups[fromGroupIndex].files = newGroups[fromGroupIndex].files.filter(img => img !== image);
    newGroups[toGroupIndex].files.push(image);
    const filteredGroups = newGroups.filter(group => group.files.length > 0);
    setGroups(filteredGroups);
  };

  const handleRemoveImage = (image, fromGroupIndex) => {
    const newGroups = groups.map(group => ({ ...group, files: [...group.files] }));
    newGroups[fromGroupIndex].files = newGroups[fromGroupIndex].files.filter(img => img !== image);
    const filteredGroups = newGroups.filter(group => group.files.length > 0);
    setGroups(filteredGroups);
  };

  const handleRemoveGroup = (groupIndex) => {
    if (window.confirm("Are you sure you want to remove this group?")) {
      const newGroups = groups.filter((_, index) => index !== groupIndex);
      setGroups(newGroups);
    }
  };

  const handleTextChange = (groupIndex, text) => {
    const newGroups = groups.map((group, index) =>
      index === groupIndex ? { ...group, text } : group
    );
    setGroups(newGroups);
  };

  const handleImageClick = (groupIndex, imageIndex) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      let convertedFile = file;
      if (isHeicFile(file)) {
        const jpegBlob = await convertHEICToJPEG(file);
        if (jpegBlob) {
          convertedFile = new File([jpegBlob], file.name.replace(/\.[^/.]+$/, ".jpg"), { type: "image/jpeg" });
        }
      }

      // Resize large images to prevent page breaking
      convertedFile = await resizeImage(convertedFile);

      const newImage = {
        file: convertedFile,
        time: convertedFile.lastModified,
        preview: URL.createObjectURL(convertedFile),
      };
      const newGroups = groups.map((group, index) => {
        if (index === groupIndex) {
          const newFiles = [...group.files];
          newFiles[imageIndex] = newImage;
          return { ...group, files: newFiles };
        }
        return group;
      });
      setGroups(newGroups);
    };
    input.click();
  };

  const handleSubmitGroup = async (groupIndex) => {
    // Guard against double-clicks/double-taps firing this twice before the
    // isLoading state update (below) has committed and re-rendered the button
    // as disabled — that race used to produce two separate product uploads
    // from the same images. Set/read synchronously via a ref, not state.
    if (submittingGroupsRef.current.has(groupIndex)) return;
    submittingGroupsRef.current.add(groupIndex);

    // Generate the product's Firestore doc ID up front (no network round trip —
    // just a locally-generated ID) so we can tell the product list page exactly
    // which doc to watch instead of waiting for the first image upload to
    // resolve and tell us the server-assigned ID.
    const clientProductId = doc(collection(db, `customers/${customer.id}/products`)).id;

    if (!bulkMode) {
      // Single-item mode: don't make the user sit and watch progress bars —
      // send them to their product list right away and let the upload finish
      // in the background (the network requests aren't tied to this component
      // staying mounted). Pass the pending ID + a local preview so the list
      // page can show a placeholder card immediately and fill it in live.
      navigate(`/products-v2/${user.uid}`, {
        state: {
          pendingProductId: clientProductId,
          pendingPreviewUrl: groups[groupIndex]?.files?.[0]?.preview || null,
        },
      });
    }
    setGroups(prevGroups => {
      const newGroups = [...prevGroups];
      newGroups[groupIndex].isLoading = true;
      return newGroups;
    });
    try {
      const group = groups[groupIndex];
      const uploads = group.files;
      let productDocument = clientProductId;
      setProcessingIndex(groupIndex);
      activeUploadRef.current = { groupIndex, productDocument };
      const accumulatedResults = [];

      for (let i = 0; i < uploads.length; i++) {
        const upload = uploads[i];
        const base64Image = await encodeFileAsBase64(upload.file);
        const result = await submitPhoto({
          input: base64Image,
          speechInput: group.text,
          userId: auth.currentUser.uid,
          customerId:  customer.customerId,
          'expectedImageCount': uploads.length,
          productDocument: productDocument,
          currentImageCount: i,
        });

        accumulatedResults.push(result);

        const resultData = result['data'];
        productDocument = resultData.productDoc;
        activeUploadRef.current = { groupIndex, productDocument };
        const dict = {
          'imageUrl': resultData.imageUrl,
          'photoIndex': i,
          'expectedImageCount': uploads.length,
          'productDocument': resultData['productDoc'],
          'productImageDocument': resultData['imageDoc'],
          'customerId': customer.id,
          'userId': shopikUser.userId,
          'sku': sku,
          'appVersion': '3.3'
        };

        if ((uploaderStyle === 1) && (user.instagram)) {
          dict['additionalShopifyTags'] = '@' + user.instagram;
        }
        console.log(dict)
        await appHandoff(dict).then((innerData) => {
          console.log('demo handed off to app ' + innerData);
        });
      }

      setGroups(prevGroups => {
        const newGroups = prevGroups.map((group, index) =>
          index === groupIndex ? { ...group, productDocument, isLoading: false } : group
        );
        return newGroups;
      });

      activeUploadRef.current = { groupIndex: -1, productDocument: '' }; // fully uploaded, safe to leave
      setProcessingIndex(-1); // Reset processing index after submission
      // Bulk mode: stay put so the user can keep submitting other groups —
      // they leave via the "View My Products" button once they're done.
      submittingGroupsRef.current.delete(groupIndex);
    } catch (error) {
      console.error('Error processing files:', error);
      // The upload didn't finish, so the product (if one was created) is incomplete
      // and will never process — clean it up rather than leaving an orphaned record.
      const { productDocument: partialDoc } = activeUploadRef.current;
      if (partialDoc) {
        deleteProduct(customer.id, partialDoc).catch((delError) =>
          console.error('Error deleting incomplete product after failed upload:', delError)
        );
      }
      activeUploadRef.current = { groupIndex: -1, productDocument: '' };
      setGroups(prevGroups => {
        const newGroups = [...prevGroups];
        newGroups[groupIndex].isLoading = false;
        newGroups[groupIndex].productDocument = '';
        return newGroups;
      });
      setProcessingIndex(-1); // Reset processing index on error
      submittingGroupsRef.current.delete(groupIndex);
    }
  };

  const handleSubmitAll = async () => {
    setHasSubmitted(true);
    setIsSubmittingAll(true);
    // Only take the first batch — any remaining pending groups stay put so the
    // user can hit "Submit All" again once this batch finishes.
    const batch = pendingGroupIndices.slice(0, SUBMIT_ALL_BATCH_SIZE);
    try {
      for (const groupIndex of batch) {
        await handleSubmitGroup(groupIndex);
      }
    } finally {
      setIsSubmittingAll(false);
    }
  };

  const onSortStart = () => {
    document.body.style.overflow = 'hidden'; // Disable scrolling
  };

  const onSortEnd = ({ oldIndex, newIndex, groupIndex }) => {
    const newGroups = groups.map((group, index) => ({
      ...group,
      files: arrayMoveImmutable(group.files, oldIndex, newIndex),
    }));
    setGroups(newGroups);
  };

  const updateState = (groupIndex) => {
    // Use setGroups with prevGroups to ensure you are modifying the current state
    setGroups(prevGroups => {
      // Create a copy of the previous groups to avoid directly mutating state
      const newGroups = [...prevGroups];
      
      // Set the isProcessed property to true for the group at the given index
      newGroups[groupIndex] = {
        ...newGroups[groupIndex], // Ensure the rest of the group's properties remain unchanged
        isProcessed: true,        // Update the isProcessed property
      };
      
      // Return the new groups array to update the state
      return newGroups;
    });
  };

  const insertNewGroup = (groupIndex, imageIndex) => {
    const beforeSplit = groups[groupIndex].files.slice(0, imageIndex + 1);
    const afterSplit = groups[groupIndex].files.slice(imageIndex + 1);
    const newGroups = [...groups];
    
    const newGroup = { files: afterSplit, text: '', productDocument: '' };
    
    newGroups.splice(groupIndex + 1, 0, newGroup);
    
    newGroups[groupIndex] = { ...newGroups[groupIndex], files: beforeSplit };

    const filteredGroups = newGroups.filter(group => group.files.length > 0);

    setGroups(filteredGroups);
  };

  return (
    <DndProvider backend={isTouchDevice() ? TouchBackend : HTML5Backend}>
    <CustomDragLayer />
    <Container maxWidth="lg"  sx={{
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    }}>
    {(customer.hasActiveSubscription == null  ||  customer.hasActiveSubscription ||  customer.overrideSubscription) ? (
      <>
      {groups.length === 0 && !loadingFiles && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Add Item</Typography>
          <Typography variant="body2" color="text.secondary">Upload photos of your item to get started.</Typography>
        </Box>
      )}
      {(customer.uploaderStyle === 1 && groups.length === 0) && (
        <Typography variant="h5" align="center" gutterBottom>
          You’re in! Upload your product pics and let the AI do its thing!
        </Typography>
      )}
      <>
      {groups.length === 0 && uploaderStyle !== 1 && !loadingFiles && (
        <>
          <Box sx={{ mb: 1 }}>
            <ToggleButtonGroup
              value={bulkMode ? 'bulk' : 'single'}
              exclusive
              size="small"
              onChange={(e, newValue) => {
                if (newValue !== null) setBulkMode(newValue === 'bulk');
              }}
            >
              <ToggleButton value="single">Single Item</ToggleButton>
              <ToggleButton value="bulk">Bulk Upload</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {bulkMode && (
            <Box id="default-uploader-settings" sx={{ mt: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Grouping Settings
              </Typography>
              <TextField
                label="Default max number of photos per group"
                type="number"
                value={defaultPhotoCount}
                onChange={(e) => setDefaultPhotoCount(parseInt(e.target.value, 10) || 0)}
                margin="normal"
                fullWidth
              />

              <TextField
                label="Time between photo groups (seconds)"
                type="number"
                value={defaultTime / 1000}
                onChange={(e) => setDefaultTime((parseInt(e.target.value, 10) || 0) * 1000)}
                margin="normal"
                fullWidth
              />

              <TextField
                label="Default text for all groups/products"
                value={defaultText}
                onChange={(e) => setDefaultText(e.target.value)}
                margin="normal"
                fullWidth
              />
            </Box>
          )}
        </>
      )}
      </>
      <div className="su-groups">
        {groups.map((group, groupIndex) => (
          group.productDocument ? (
            <div key={groupIndex} className="su-group-card" style={{ gridColumn: '1 / -1' }}>
              {editingGroups.has(groupIndex) ? (
                <ProductEditGeneric
                  customerId={customer.id}
                  productId={group.productDocument}
                  updateState={(updatedData) => updateState(groupIndex, updatedData)}
                  uploaderStyle={uploaderStyle}
                  groupIndex={groupIndex}
                />
              ) : (
                <UploaderProductPreview
                  customerId={customer.id}
                  productId={group.productDocument}
                  onEdit={() => setEditingGroups((prev) => new Set(prev).add(groupIndex))}
                />
              )}
            </div>
          ) : (
            <div key={groupIndex} className="su-group-card">
              {bulkMode && (
                <div className="su-group-header">
                  <span className="su-group-title">Product {groupIndex + 1}</span>
                  <IconButton
                    size="small"
                    title="Remove product"
                    onClick={() => handleRemoveGroup(groupIndex)}
                    sx={{ color: '#111111' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </div>
              )}

              <DroppableImageList
                items={group.files}
                groupIndex={groupIndex}
                groups={groups}
                moveImage={moveImage}
                handleImageClick={handleImageClick}
                handleRemoveImage={handleRemoveImage}
                handleMoveImage={handleMoveImage}
                insertNewGroup={insertNewGroup}
                bulkMode={bulkMode}
              />

              <div className="su-group-footer">
                <TextField
                  label="Item description (optional)"
                  value={group.text}
                  onChange={(e) => handleTextChange(groupIndex, e.target.value)}
                  fullWidth
                  size="small"
                  multiline
                  minRows={1}
                  maxRows={3}
                />
                <Button
                  variant="contained"
                  onClick={() => handleSubmitGroup(groupIndex)}
                  disabled={group.isLoading || group.files.length === 0 || isSubmittingAll}
                  sx={{ backgroundColor: '#111111', '&:hover': { backgroundColor: '#000000' } }}
                >
                  {group.isLoading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Submit Product'}
                </Button>
              </div>
            </div>
          )
        ))}

        {loadingFiles ? (
          <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', marginTop: 20 }}>
            <CircularProgress />
          </div>
        ) : anyProductSubmitted ? null : (!bulkMode && groups.length > 0) ? null : (
          <Dropzone onDrop={handleDrop}>
            {({ getRootProps, getInputProps }) => (
              groups.length === 0 ? (
                <div {...getRootProps({ className: 'su-primary-dropzone' })} style={{ gridColumn: '1 / -1' }}>
                  <input {...getInputProps()} />
                  <AddPhotoAlternateIcon sx={{ fontSize: 40, color: '#111111', opacity: 0.7 }} />
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#111111' }}>
                    Drag & drop your product photos here
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Photos are auto-grouped into products, or click to browse
                  </Typography>
                </div>
              ) : (
                <div {...getRootProps({ className: 'su-compact-dropzone' })}>
                  <input {...getInputProps()} />
                  <AddPhotoAlternateIcon sx={{ color: '#111111' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Add more products
                  </Typography>
                </div>
              )
            )}
          </Dropzone>
        )}
      </div>

      {bulkMode && pendingGroupIndices.length > 1 && (
        <Tooltip
          title={
            pendingGroupIndices.length > SUBMIT_ALL_BATCH_SIZE
              ? `You can only submit up to ${SUBMIT_ALL_BATCH_SIZE} products at once. Submit a few individually, or split/remove groups to get under the limit.`
              : ''
          }
        >
          <span>
            <Button
              variant="contained"
              onClick={handleSubmitAll}
              disabled={
                isSubmittingAll ||
                pendingGroupIndices.length === 0 ||
                pendingGroupIndices.length > SUBMIT_ALL_BATCH_SIZE
              }
              sx={{ marginTop: 2, marginRight: 2, backgroundColor: '#111111', '&:hover': { backgroundColor: '#000000' } }}
            >
              {isSubmittingAll ? (
                <CircularProgress size={24} sx={{ color: '#fff' }} />
              ) : (
                `Submit All (up to ${SUBMIT_ALL_BATCH_SIZE} at a time)`
              )}
            </Button>
          </span>
        </Tooltip>
      )}

      {bulkMode && anyProductSubmitted && (
        <Button
          variant="contained"
          onClick={() => navigate(`/products-v2/${user.uid}`)}
          sx={{ marginTop: 2, backgroundColor: '#111111', '&:hover': { backgroundColor: '#000000' } }}
        >
          View My Products
        </Button>
      )}
      </>
    ) : (
      <><br/>You need a plan to refresh your credits<br/>
             <Button
            variant="contained"
            sx={{
                backgroundColor: '#111111',
                color: 'white',
                textTransform: 'none',
                ':hover': {
                    backgroundColor: '#000000',
                },
                marginTop: 2,
            }}
            onClick={goToPlanPage}
        >
            See All Plans
        </Button>
            </>
    )}

    <Dialog open={blocker.state === 'blocked'} maxWidth="xs" fullWidth>
      <DialogTitle>Upload not finished</DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          This item hasn't finished uploading yet. Leaving now will leave it stuck and it won't process correctly.
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={1}>
          If you leave anyway, this partial item will be deleted so it doesn't get left behind broken.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Button onClick={() => blocker.reset?.()} variant="outlined" color="inherit" disabled={deletingOnLeave}>
          Keep uploading
        </Button>
        <Button onClick={handleConfirmLeave} variant="contained" color="error" disabled={deletingOnLeave}>
          {deletingOnLeave ? <CircularProgress size={18} color="inherit" /> : 'Leave & delete'}
        </Button>
      </DialogActions>
    </Dialog>
    </Container>
    </DndProvider>
  );
}

export default Uploader;